const MEAL_TYPE_ORDER = [
  "ROOM_ONLY",
  "BREAKFAST",
  "HALF_BOARD",
  "FULL_BOARD",
  "ALL_INCLUSIVE",
  "LUNCH",
  "DINNER",
];

const MEAL_TYPE_LABELS = {
  ROOM_ONLY: "Room Only",
  BREAKFAST: "Breakfast Included",
  HALF_BOARD: "Half Board",
  FULL_BOARD: "Full Board",
  ALL_INCLUSIVE: "All Inclusive",
  LUNCH: "Lunch",
  DINNER: "Dinner",
};

const MEAL_ALIAS_TO_KEY = {
  "ROOM ONLY": "ROOM_ONLY",
  ROOMONLY: "ROOM_ONLY",
  "ONLY ROOM": "ROOM_ONLY",
  RO: "ROOM_ONLY",
  EP: "ROOM_ONLY",
  "EUROPEAN PLAN": "ROOM_ONLY",
  BREAKFAST: "BREAKFAST",
  "BREAKFAST INCLUDED": "BREAKFAST",
  "BED AND BREAKFAST": "BREAKFAST",
  "B&B": "BREAKFAST",
  BB: "BREAKFAST",
  CB: "BREAKFAST",
  CP: "BREAKFAST",
  "CONTINENTAL PLAN": "BREAKFAST",
  "CONTINENTAL BREAKFAST": "BREAKFAST",
  "HALF BOARD": "HALF_BOARD",
  HALFBOARD: "HALF_BOARD",
  HB: "HALF_BOARD",
  MAP: "HALF_BOARD",
  "MODIFIED AMERICAN PLAN": "HALF_BOARD",
  "FULL BOARD": "FULL_BOARD",
  FULLBOARD: "FULL_BOARD",
  FB: "FULL_BOARD",
  AP: "FULL_BOARD",
  "AMERICAN PLAN": "FULL_BOARD",
  "ALL INCLUSIVE": "ALL_INCLUSIVE",
  ALLINCLUSIVE: "ALL_INCLUSIVE",
  AI: "ALL_INCLUSIVE",
  LUNCH: "LUNCH",
  DINNER: "DINNER",
  NONE: "ROOM_ONLY",
};

export const normalizeMealTypeKey = (value) => {
  const normalized = String(value ?? "")
    .trim()
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .toUpperCase();
  if (!normalized) return null;
  if (MEAL_ALIAS_TO_KEY[normalized]) {
    return MEAL_ALIAS_TO_KEY[normalized];
  }
  if (
    /\b(ROOM\s*ONLY|ONLY\s*ROOM|EUROPEAN\s*PLAN|\bEP\b|\bRO\b)\b/.test(
      normalized,
    ) &&
    !/\b(BREAKFAST|HALF\s*BOARD|FULL\s*BOARD|DINNER|ALL\s*INCLUSIVE|\bHB\b|\bFB\b|\bBB\b)\b/.test(
      normalized,
    )
  ) {
    return "ROOM_ONLY";
  }
  if (/\b(HALF\s*BOARD|MODIFIED\s*AMERICAN|\bHB\b|\bMAP\b)\b/.test(normalized)) {
    return "HALF_BOARD";
  }
  if (/\b(FULL\s*BOARD|AMERICAN\s*PLAN|\bFB\b|\bAP\b)\b/.test(normalized)) {
    return "FULL_BOARD";
  }
  if (/\bALL\s*INCLUSIVE\b|\bAI\b/.test(normalized)) {
    return "ALL_INCLUSIVE";
  }
  if (
    /\b(BREAKFAST|CONTINENTAL|\bBB\b|\bCB\b|\bCP\b|B\s*&\s*B)\b/.test(
      normalized,
    )
  ) {
    return "BREAKFAST";
  }
  if (/\bDINNER\b/.test(normalized)) {
    return "DINNER";
  }
  if (/\bLUNCH\b/.test(normalized)) {
    return "LUNCH";
  }
  return null;
};

export const formatMealTypeLabel = (mealKey) =>
  MEAL_TYPE_LABELS[mealKey] ?? null;

const collectTextCandidates = (ratePlan) => {
  if (!ratePlan) return [];
  const list = [
    ratePlan?.mealPlan,
    ratePlan?.MealPlan,
    ratePlan?.ratePlanName,
    ratePlan?.RatePlanName,
    ratePlan?.rateType,
    ratePlan?.RateType,
    ratePlan?.boardBasis,
    ratePlan?.BoardBasis,
    ratePlan?.boardName,
    ratePlan?.BoardName,
  ];
  const benefits = ratePlan?.benefits ?? ratePlan?.Benefits;
  if (Array.isArray(benefits)) {
    benefits.forEach((b) => {
      if (typeof b === "string") list.push(b);
      else if (b && typeof b === "object") {
        list.push(b?.name ?? b?.Name ?? b?.label ?? b?.Label ?? "");
      }
    });
  }
  const inclusions = ratePlan?.inclusions ?? ratePlan?.Inclusions;
  if (Array.isArray(inclusions)) {
    inclusions.forEach((item) => {
      if (typeof item === "string") list.push(item);
    });
  }
  return list.filter(Boolean);
};

export const extractMealTypeKeyFromRatePlan = (ratePlan) => {
  if (!ratePlan) return null;
  for (const candidate of collectTextCandidates(ratePlan)) {
    const key = normalizeMealTypeKey(candidate);
    if (key) return key;
  }
  return null;
};

/** Meal/board category under room name (Checkout / Preview / Tickets). */
export const getRoomMealCategoryLabel = (room, ratePlan = null) => {
  const rp =
    ratePlan ??
    room?.ratePlans?.[0] ??
    room?.RatePlans?.[0] ??
    null;
  const fromTicketFields = {
    mealPlan: room?.mealPlan ?? room?.MealPlan,
    MealPlan: room?.MealPlan,
    ratePlanName: room?.ratePlanName ?? room?.RatePlanName,
    RatePlanName: room?.RatePlanName,
    boardBasis: room?.boardBasis ?? room?.BoardBasis,
    BoardBasis: room?.BoardBasis,
  };
  const key =
    extractMealTypeKeyFromRatePlan(rp) ||
    extractMealTypeKeyFromRatePlan(fromTicketFields) ||
    normalizeMealTypeKey(room?.roomDesc ?? room?.RoomDesc);
  return key ? formatMealTypeLabel(key) : "";
};

/** Stamp mealPlan from checkout roomPlan onto price rooms when API omits it. */
export const applyMealCategoryFromRoomPlan = (hotels, roomPlan = []) => {
  if (!hotels || !Array.isArray(hotels.rooms) || hotels.rooms.length === 0) {
    return hotels;
  }
  const plans = Array.isArray(roomPlan) ? roomPlan : [];
  if (plans.length === 0) return hotels;

  const rooms = hotels.rooms.map((room, index) => {
    const plan = plans[index] ?? plans[0] ?? {};
    const mealRaw = String(plan?.mealPlan ?? plan?.MealPlan ?? "").trim();
    if (!mealRaw) return room;

    const existing = getRoomMealCategoryLabel(room);
    if (existing) return room;

    const ratePlans = Array.isArray(room?.ratePlans)
      ? room.ratePlans.map((rp, rpIdx) =>
          rpIdx === 0
            ? {
                ...rp,
                mealPlan: rp?.mealPlan || mealRaw,
                MealPlan: rp?.MealPlan || mealRaw,
                ratePlanName: rp?.ratePlanName || mealRaw,
              }
            : rp,
        )
      : [{ mealPlan: mealRaw, ratePlanName: mealRaw }];

    return {
      ...room,
      mealPlan: room?.mealPlan || mealRaw,
      ratePlans,
    };
  });

  return { ...hotels, rooms };
};

const getRoomRatePlans = (room) => {
  if (Array.isArray(room?.ratePlans)) return room.ratePlans;
  if (Array.isArray(room?.RatePlans)) return room.RatePlans;
  if (Array.isArray(room?.rooms)) {
    return room.rooms.flatMap((nested) => getRoomRatePlans(nested));
  }
  return [];
};

export const collectCanonicalMealTypes = (rooms = []) => {
  const keys = new Set();
  (Array.isArray(rooms) ? rooms : []).forEach((room) => {
    getRoomRatePlans(room).forEach((rp) => {
      const key = extractMealTypeKeyFromRatePlan(rp);
      if (key) keys.add(key);
    });
  });
  return MEAL_TYPE_ORDER.filter((key) => keys.has(key)).map((key) => ({
    value: MEAL_TYPE_LABELS[key],
    label: MEAL_TYPE_LABELS[key],
    key,
  }));
};

/** Merge room-derived meals with API availableMealTypes (never drop API options). */
export const mergeMealTypeFilterOptions = (
  rooms = [],
  availableMealTypes = [],
) => {
  const keys = new Set();
  collectCanonicalMealTypes(rooms).forEach((item) => {
    if (item?.key) keys.add(item.key);
  });
  (Array.isArray(availableMealTypes) ? availableMealTypes : []).forEach((raw) => {
    const key = normalizeMealTypeKey(raw);
    if (key) keys.add(key);
  });
  return MEAL_TYPE_ORDER.filter((key) => keys.has(key)).map((key) => ({
    value: MEAL_TYPE_LABELS[key],
    label: MEAL_TYPE_LABELS[key],
    key,
  }));
};

export const collectCanonicalMealTypeLabels = (rooms = []) =>
  collectCanonicalMealTypes(rooms).map((item) => item.label);

export const ratePlanMatchesMealFilter = (ratePlan, mealTypeFilter) => {
  if (!mealTypeFilter || !ratePlan) return true;
  const filterKey = normalizeMealTypeKey(mealTypeFilter);
  if (!filterKey) {
    return String(mealTypeFilter).trim().length === 0;
  }
  return extractMealTypeKeyFromRatePlan(ratePlan) === filterKey;
};

/** Explicit rate/room PAN flag only — hotel-level IN nationality must not blank the filter.
 *  Tripjack ops use `ipr` (is PAN required); optional rates have ipr=false / omitted. */
const readPanFlag = (value) => {
  if (value === true || value === 1 || value === "true" || value === "1") {
    return true;
  }
  if (value === false || value === 0 || value === "false" || value === "0") {
    return false;
  }
  return null;
};

export const isRatePlanPanRequired = (ratePlan, room = null) => {
  const sources = [ratePlan, room];
  for (const src of sources) {
    if (!src || typeof src !== "object") continue;
    const flags = [
      readPanFlag(src.panRequired),
      readPanFlag(src.PanRequired),
      readPanFlag(src.pan),
      readPanFlag(src.PAN),
      readPanFlag(src.Pan),
      readPanFlag(src.ipr),
      readPanFlag(src.IPR),
    ];
    if (flags.some((f) => f === false)) return false;
    if (flags.some((f) => f === true)) return true;
  }
  return false;
};

/** Tripjack PAN-optional rate — used for Without-PAN +2% markup. */
export const isRatePlanExplicitlyPanOptional = (ratePlan, room = null) => {
  const sources = [ratePlan, room];
  return sources.some((src) => {
    if (!src || typeof src !== "object") return false;
    return (
      readPanFlag(src.panRequired) === false ||
      readPanFlag(src.PanRequired) === false ||
      readPanFlag(src.pan) === false ||
      readPanFlag(src.PAN) === false ||
      readPanFlag(src.Pan) === false ||
      readPanFlag(src.ipr) === false ||
      readPanFlag(src.IPR) === false
    );
  });
};

/** Details-page roomPlan.panRequired: true / false / null (unknown). */
export const resolveRoomPlanPanRequired = (roomPlan = []) => {
  if (!Array.isArray(roomPlan) || roomPlan.length === 0) return null;
  const flags = roomPlan
    .map((p) => {
      if (!p || typeof p !== "object") return null;
      if (p.panRequired === true || p.PanRequired === true) return true;
      if (p.panRequired === false || p.PanRequired === false) return false;
      return null;
    })
    .filter((v) => v !== null);
  if (flags.length === 0) return null;
  return flags.some((v) => v === true);
};

/**
 * Stamp Details selection (PAN + refundable) onto hotelprice rooms / guestRequiredFields.
 * Fixes inverted PAN ask and NR→green Free Cancel on checkout.
 */
export const applyRoomPlanSelectionFlagsToHotels = (hotels, roomPlan = []) => {
  if (!hotels || typeof hotels !== "object") return hotels;
  if (!Array.isArray(roomPlan) || roomPlan.length === 0) return hotels;

  const roomsKey = Array.isArray(hotels.rooms)
    ? "rooms"
    : Array.isArray(hotels.Rooms)
      ? "Rooms"
      : null;
  const rooms = roomsKey ? hotels[roomsKey] : [];
  const nextRooms = rooms.map((room, index) => {
    const plan = roomPlan[index] ?? roomPlan[0] ?? {};
    const plans = Array.isArray(room?.ratePlans) ? room.ratePlans : [];
    const nextPlans = plans.map((rp, rpIdx) => {
      if (rpIdx !== 0 || !rp) return rp;
      let next = { ...rp };
      if (plan.panRequired === true || plan.PanRequired === true) {
        next = {
          ...next,
          panRequired: true,
          PanRequired: true,
          pan: true,
          PAN: true,
          ipr: true,
        };
      } else if (plan.panRequired === false || plan.PanRequired === false) {
        next = {
          ...next,
          panRequired: false,
          PanRequired: false,
          pan: false,
          PAN: false,
          ipr: false,
        };
      }
      if (plan.refundable === false || plan.Refundable === false) {
        next = {
          ...next,
          refundable: false,
          Refundable: false,
          tripjackIfra: false,
        };
        delete next.lastCancellationDate;
        delete next.LastCancellationDate;
        delete next.lastVoucherDate;
        delete next.LastVoucherDate;
      } else if (plan.refundable === true || plan.Refundable === true) {
        // Clear stale tripjackIfra:false from hotelprice — otherwise checkout
        // paints Non-refundable even when Details selection was Free Cancel.
        next = {
          ...next,
          refundable: true,
          Refundable: true,
          tripjackIfra: true,
          TripjackIfra: true,
        };
        // hotelprice may attach NR-only scnp/stubs from a wrong rate match —
        // strip those so checkout can enrich real FC slabs (like Preview).
        const scnp = String(
          next.supplierCancellationNarrative ??
            next.SupplierCancellationNarrative ??
            "",
        ).trim();
        if (/^non[\s-]*refundable\.?$/i.test(scnp)) {
          delete next.supplierCancellationNarrative;
          delete next.SupplierCancellationNarrative;
        }
        if (plan.lastCancellationDate) {
          next.lastCancellationDate = plan.lastCancellationDate;
          next.LastCancellationDate = plan.lastCancellationDate;
          next.lastVoucherDate = plan.lastCancellationDate;
          next.LastVoucherDate = plan.lastCancellationDate;
        } else {
          // Details did not show a Free Cancellation deadline — don't keep
          // a hotelprice leftover date that would paint green FC on checkout.
          delete next.lastCancellationDate;
          delete next.LastCancellationDate;
        }
      }
      return next;
    });
    const roomOut = { ...room, ratePlans: nextPlans };
    if (plan.panRequired === true || plan.PanRequired === true) {
      roomOut.panRequired = true;
      roomOut.PanRequired = true;
    } else if (plan.panRequired === false || plan.PanRequired === false) {
      roomOut.panRequired = false;
      roomOut.PanRequired = false;
    }
    return roomOut;
  });

  const planPan = resolveRoomPlanPanRequired(roomPlan);
  let guestRequiredFields =
    hotels.guestRequiredFields ?? hotels.GuestRequiredFields;
  if (planPan !== null) {
    const base =
      Array.isArray(guestRequiredFields) && guestRequiredFields.length > 0
        ? guestRequiredFields
        : [{ guestMode: "All" }];
    guestRequiredFields = base.map((row) => ({
      ...row,
      pan: planPan,
      PAN: planPan,
    }));
  }

  return {
    ...hotels,
    ...(roomsKey ? { [roomsKey]: nextRooms } : {}),
    ...(guestRequiredFields
      ? { guestRequiredFields, GuestRequiredFields: guestRequiredFields }
      : {}),
  };
};
